export const CELL_SELECTOR = '[data-sortable-cell]';
export const CONTENT_SELECTOR = '[data-sortable-content]';

export function groupCellsByColumnIndex(table: HTMLElement): HTMLElement[][] {
	const byKey = new Map<string, HTMLElement[]>();
	// Group the cells by the column index
	table.querySelectorAll<HTMLElement>(CELL_SELECTOR).forEach(cell => {
		const key = cell.dataset['columnIndex'] ?? '0';
		if (!byKey.has(key)) {
			byKey.set(key, []);
		}
		byKey.get(key)?.push(cell);
	});

	return [...byKey.entries()].sort(([a], [b]) => Number(a) - Number(b)).map(([, cells]) => cells);
}

export function measureNaturalColumnWidths(table: HTMLElement): number[] {
	table.dataset['intrinsicMeasure'] = 'true';

	try {
		const colCells = groupCellsByColumnIndex(table);
		return colCells.map(cells =>
			Math.max(0, ...cells.map(cell => (cell.offsetParent ? cell.offsetWidth : 0)))
		);
	} finally {
		delete table.dataset['intrinsicMeasure'];
	}
}

export function isColumnVisible(cells: HTMLElement[]): boolean {
	return cells.some(cell => cell.offsetParent !== null);
}

export function getVisibleColumnIndices(table: HTMLElement): number[] {
	const colCells = groupCellsByColumnIndex(table);
	// Get the visible column indices
	return colCells.reduce<number[]>((indices, cells, index) => {
		// If the column is visible, add the index to the indices
		if (isColumnVisible(cells)) {
			indices.push(index);
		}
		return indices;
	}, []);
}

export function pickNaturalWidthsByIndices(
	naturalWidths: number[],
	visibleIndices: number[]
): number[] {
	return visibleIndices.map(index => naturalWidths[index] ?? 0);
}

export function mapWidthsToAllColumns(
	columnCount: number,
	visibleIndices: number[],
	visibleWidths: number[]
): (number | undefined)[] {
	const widths: (number | undefined)[] = Array.from({ length: columnCount });
	visibleIndices.forEach((colIndex, visibleIndex) => {
		widths[colIndex] = visibleWidths[visibleIndex];
	});
	return widths;
}

export function getContentPaddingInline(table: HTMLElement): { left: number; right: number } {
	const contentWrapper = table.querySelector<HTMLElement>(CONTENT_SELECTOR);

	if (!contentWrapper) {
		return { left: 0, right: 0 };
	}

	const styles = getComputedStyle(contentWrapper);

	return {
		left: Number.parseFloat(styles.paddingLeft) || 0,
		right: Number.parseFloat(styles.paddingRight) || 0,
	};
}

export function getRowLayoutWidth(table: HTMLElement, scrollEl: HTMLElement): number {
	const { left, right } = getContentPaddingInline(table);
	const paddingInline = left + right;

	if (paddingInline > 0) {
		return Math.max(0, scrollEl.clientWidth - paddingInline);
	}

	return scrollEl.clientWidth;
}
